// Phase 9A.9.1 — Signal calibration formatting helpers.
//
// Pure deterministic helpers for the curated Signal Health surface.
// NO React. NO Supabase. NO clock. NO side effects.
//
// All helpers operate on the curated summary shape returned by the
// `get_signal_calibration_summary` RPC. They CANNOT touch raw Brier
// score, CLV, closing-line, or provider evidence — none of those
// fields exist on the surface.

export const SIGNAL_CALIBRATION_VERSION = "signal-calibration-summary-v1";
export const MINIMUM_FOR_BUILDING = 10;
export const MINIMUM_FOR_READY = 50;
// Phase 9A.10 — freshness window. The RPC re-evaluates this constant
// server-side; the client surfaces it for copy + verifier symmetry.
export const FRESHNESS_WINDOW_DAYS = 7;

export type SignalCalibrationStatus = "insufficient" | "building" | "ready" | "cooling";

export interface SignalCalibrationSettledCounts {
  settled_positive: number;
  settled_negative: number;
}

export interface SignalCalibrationDistributions {
  risk: Readonly<Record<string, number>>;
  confidence: Readonly<Record<string, number>>;
  data_tier: Readonly<Record<string, number>>;
}

export interface SignalCalibrationCopy {
  title: string;
  summary: string;
  disclaimer: string;
}

export interface SignalCalibrationSummary {
  version: string;
  status: SignalCalibrationStatus;
  sample_size: {
    signals_graded: number;
    minimum_for_building: number;
    minimum_for_ready: number;
  };
  settled_counts: SignalCalibrationSettledCounts | null;
  distributions: SignalCalibrationDistributions | null;
  average_prepared_probability: number | null;
  updated_through: string | null;
  copy: SignalCalibrationCopy;
}

// Coerce the wire shape (jsonb from PostgREST) to the typed surface
// without trusting field types blindly. Unknown keys are dropped.
// The verifier asserts the wire shape matches the safe-allow-list
// independently — this helper is defensive only.
export function projectSignalCalibrationSummary(raw: unknown): SignalCalibrationSummary | null {
  if (raw === null || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.version !== SIGNAL_CALIBRATION_VERSION) return null;
  const status = r.status;
  if (
    status !== "insufficient" &&
    status !== "building" &&
    status !== "ready" &&
    status !== "cooling"
  ) {
    return null;
  }

  const sample = r.sample_size;
  if (sample === null || typeof sample !== "object") return null;
  const s = sample as Record<string, unknown>;

  const copy = r.copy;
  if (copy === null || typeof copy !== "object") return null;
  const c = copy as Record<string, unknown>;

  return {
    version: SIGNAL_CALIBRATION_VERSION,
    status,
    sample_size: {
      signals_graded: typeof s.signals_graded === "number" ? s.signals_graded : 0,
      minimum_for_building:
        typeof s.minimum_for_building === "number" ? s.minimum_for_building : MINIMUM_FOR_BUILDING,
      minimum_for_ready:
        typeof s.minimum_for_ready === "number" ? s.minimum_for_ready : MINIMUM_FOR_READY,
    },
    settled_counts: projectSettledCounts(r.settled_counts),
    distributions: projectDistributions(r.distributions),
    average_prepared_probability:
      typeof r.average_prepared_probability === "number" ? r.average_prepared_probability : null,
    updated_through: typeof r.updated_through === "string" ? r.updated_through : null,
    copy: {
      title: typeof c.title === "string" ? c.title : "Signal health",
      summary: typeof c.summary === "string" ? c.summary : "Sample size still building.",
      disclaimer:
        typeof c.disclaimer === "string"
          ? c.disclaimer
          : "This is not a profit record. Manual placement only. No bookmaker connection.",
    },
  };
}

function projectSettledCounts(raw: unknown): SignalCalibrationSettledCounts | null {
  if (raw === null || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.settled_positive !== "number" || typeof r.settled_negative !== "number") {
    return null;
  }
  return {
    settled_positive: r.settled_positive,
    settled_negative: r.settled_negative,
  };
}

function projectDistributions(raw: unknown): SignalCalibrationDistributions | null {
  if (raw === null || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return {
    risk: pickStringNumberMap(r.risk),
    confidence: pickStringNumberMap(r.confidence),
    data_tier: pickStringNumberMap(r.data_tier),
  };
}

function pickStringNumberMap(raw: unknown): Record<string, number> {
  if (raw === null || typeof raw !== "object") return {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "number") out[k] = v;
  }
  return out;
}

export function statusLabel(status: SignalCalibrationStatus): string {
  if (status === "ready") return "Calibration snapshot";
  if (status === "cooling") return "Cooling";
  if (status === "building") return "Sample size building";
  return "Sample size still building";
}

export function statusBodyCopy(status: SignalCalibrationStatus): string {
  if (status === "ready") {
    return "Prepared intelligence is monitored against settled outcomes.";
  }
  if (status === "cooling") {
    return "Latest graded activity is aging. Signal health will refresh as new settled signals are graded.";
  }
  if (status === "building") {
    return "STRIKZA has started grading prepared intelligence; counts are shown but distributions are not yet available.";
  }
  return "Prepared intelligence is being monitored internally; nothing to summarize yet.";
}

// Format a settled-count line in a neutral, non-performance framing.
export function formatSettledCounts(counts: SignalCalibrationSettledCounts | null): string {
  if (counts === null) return "Settled signals so far: —";
  const total = counts.settled_positive + counts.settled_negative;
  return `Settled signals so far: ${String(total)} (positive ${String(counts.settled_positive)} · negative ${String(counts.settled_negative)})`;
}

export function formatAveragePreparedProbability(avg: number | null): string {
  if (avg === null || !Number.isFinite(avg)) return "Average prepared probability: —";
  const pct = (avg * 100).toFixed(1);
  return `Average prepared probability: ${pct}%`;
}

export function formatDistributionLine(
  label: string,
  dist: Readonly<Record<string, number>>,
): string {
  const entries = Object.entries(dist).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return `${label}: —`;
  const body = entries.map(([k, n]) => `${k} ${String(n)}`).join(" · ");
  return `${label}: ${body}`;
}

export function formatUpdatedThrough(iso: string | null): string {
  if (iso === null) return "Updated through: —";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Updated through: —";
  return `Updated through ${d.toUTCString()}`;
}

// Doctrine string asserted by the verifier alongside the disclaimer.
export const SIGNAL_HEALTH_DISCLAIMER =
  "This is not a profit record. Manual placement only. No bookmaker connection.";
